import * as Api from '@ViewModels';
import { Provider, inject } from 'mobx-react';
import momentTz from 'moment-timezone';
import * as React from 'react';
import { AdminFullscreenModalName, IModalContext, ModalChildComponentContextKey } from '../../../models';
import { IEditCampaignRequest } from '../../../models/AdminModels';
import { useEventLogging } from '../../../models/Logging';
import { useErrorMessages, useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { useEmailUpdateSendScheduleMutation } from '../../../queries';
import { ImpersonationContextViewModel } from '../../../viewmodels/AdminViewModels';
import { EditQueuedCampaign } from '../../../web/containers/EditQueuedCampaign';

interface IProps extends IModalContext {
	editRequest: IEditCampaignRequest;
}

function _EditAccountQueuedCampaign({ editRequest, parentModal }: IProps) {
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging();
	const [impersonationCtxVm] = React.useState(() => {
		const impCtx = new ImpersonationContextViewModel();
		impCtx.update(editRequest?.impersonationContext);
		return impCtx;
	});
	const impersonationContext = React.useMemo(() => {
		return impersonationCtxVm.isValid
			? {
					account: impersonationCtxVm.account,
					user: editRequest?.campaign?.creator,
				}
			: null;
	}, [impersonationCtxVm, editRequest?.campaign?.creator]);
	const sendScheduleMutation = useEmailUpdateSendScheduleMutation({
		onSuccess: campaign => {
			editRequest.campaign?.setCampaign(campaign);
			goBack();
		},
		onError: error => {
			logApiError('SetCampaignSchedule-Error', error);
			errorMessages?.pushApiError(error);
		},
	});
	const goBack = () => {
		if (parentModal?.name === AdminFullscreenModalName) {
			fullscreenModal.history.goBack();
		}
	};
	const onContextFinishedEditingClicked = (e: React.MouseEvent<HTMLElement>) => {
		/**
		 * Under impersonation,the recipients list is readonly. The default action here is to save changed back to the api.
		 * We just need to stop that and go back in the flow.
		 */
		e.preventDefault();
		goBack();
	};
	const onScheduleCtaClicked = async (e: React.MouseEvent<HTMLElement>, schedule?: Api.IScheduledSend) => {
		if (!schedule) {
			return;
		}

		/**
		 * The default action is issue a PUT request with the entire campaign. Under impersonation, we cannot do that. We
		 * need to be more targeted and edit the schedule option directly.
		 */
		e.preventDefault();
		sendScheduleMutation.mutate({
			campaignId: editRequest.campaign.id,
			impersonationContext,
			schedule,
			timeZone: momentTz.tz.guess(),
		});
	};
	return (
		<Provider impersonationContext={impersonationCtxVm}>
			<EditQueuedCampaign
				campaign={editRequest?.campaign}
				campaignGroup={editRequest?.campaignGroup}
				disableCampaignEdits={editRequest?.referrer === 'accountContentCalendar'}
				disableContactEdits={true}
				onContextFinishedEditingClicked={onContextFinishedEditingClicked}
				onScheduleCtaClicked={onScheduleCtaClicked}
				readonlyContent={true}
				requireEstimateToSend={false}
			/>
		</Provider>
	);
}

export const EditAccountQueuedCampaign = inject(ModalChildComponentContextKey)(_EditAccountQueuedCampaign);
